import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { resource as resourceMock } from "@planzone/dto";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceResource } from "../../common/resource";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { match } from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  filter: Boundaries["retrieveWorkLoads"]["request"],
): State {
  if (filter.chunkKey) return state;
  const index = state.findIndex((req) => match(filter, req.filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          filter,
          workLoads: state[index].workLoads,
          request: setRequestStateToPending(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          filter,
          workLoads: [],
          request: setRequestStateToPending(),
          isDirty: false,
        },
      ];
}

function handleSuccess(
  state: State,
  { workLoads, filter }: Boundaries["retrieveWorkLoads"]["success"],
): State {
  if (filter.chunkKey) return state;
  const index = state.findIndex((req) => match(filter, req.filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          filter,
          workLoads,
          request: setRequestStateToSuccess(),
          isDirty: false,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          filter,
          workLoads,
          request: setRequestStateToSuccess(),
          isDirty: false,
        },
      ];
}

function handleFailure(
  state: State,
  filter: Boundaries["retrieveWorkLoads"]["request"],
): State {
  if (filter.chunkKey) return state;
  const index = state.findIndex((req) => match(filter, req.filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          filter,
          workLoads: state[index].workLoads,
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          filter,
          workLoads: [],
          request: setRequestStateToFailure(),
          isDirty: false,
        },
      ];
}

function propagateEvent(state = initialState, event: Event): State {
  const isDirty =
    event.type === events.editAllocationSucceeded ||
    event.type === events.removeAllocationSucceeded ||
    event.type === events.editTaskSucceeded ||
    event.type === events.editPersonalWeeklyCalendarSucceeded ||
    event.type === events.editCalendarHolidaysSucceeded ||
    event.type === events.removeCalendarHolidaysSucceeded ||
    event.type === events.assignCalendarHolidaysToResourceSucceeded ||
    event.type === events.assignPersonalCalendarHolidaysSucceeded ||
    event.type === events.createUnavailabilitySucceeded ||
    event.type === events.editUnavailabilitySucceeded ||
    event.type === events.removeUnavailabilitySucceeded;

  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || isDirty,
    workLoads: req.workLoads.map((workLoad) => {
      const resource = reduceResource(
        {
          ...resourceMock,
          uuid: workLoad.uuid,
          name: workLoad.resourceName,
          profession: workLoad.resourceTitle || "",
        },
        event,
      );

      return {
        ...workLoad,
        uuid: resource.uuid,
        resourceName: resource.name,
        resourceTitle: resource.profession,
      };
    }),
  }));
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.retrieveWorkLoadsRequested]: handleRequest,
    [events.retrieveWorkLoadsSucceeded]: handleSuccess,
    [events.retrieveWorkLoadsFailed]: handleFailure,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
